import { z } from 'zod';

export const genePanelConfigurationIds = [
  '6:256',
  '12:128',
  '24:72',
  '36:48',
  '48:36',
  '54:32',
  '72:24',
  '80:21',
  '96:18',
  '120:14',
  '144:12',
  '216:8',
  '248:6',
  '296:5',
  '384:4',
] as const;

export type GenePanelConfigurationId = (typeof genePanelConfigurationIds)[number];

const configurationMap: Record<GenePanelConfigurationId, { genes: number; samples: number }> = {
  '6:256': { genes: 6, samples: 256 },
  '12:128': { genes: 12, samples: 128 },
  '24:72': { genes: 24, samples: 72 },
  '36:48': { genes: 36, samples: 48 },
  '48:36': { genes: 48, samples: 36 },
  '54:32': { genes: 54, samples: 32 },
  '72:24': { genes: 72, samples: 24 },
  '80:21': { genes: 80, samples: 21 },
  '96:18': { genes: 96, samples: 18 },
  '120:14': { genes: 120, samples: 14 },
  '144:12': { genes: 144, samples: 12 },
  '216:8': { genes: 216, samples: 8 },
  '248:6': { genes: 248, samples: 6 },
  '296:5': { genes: 296, samples: 5 },
  '384:4': { genes: 384, samples: 4 },
};

export const zGenePanelConfiguration = z.enum(genePanelConfigurationIds);

export function isGenePanelConfigurationId(value: string): value is GenePanelConfigurationId {
  return (genePanelConfigurationIds as readonly string[]).includes(value);
}

export function getGenePanelConfiguration(id: GenePanelConfigurationId) {
  return configurationMap[id];
}

export function parseGenePanelConfiguration(value: string): {
  id: GenePanelConfigurationId;
  genes: number;
  samples: number;
} {
  if (!isGenePanelConfigurationId(value)) {
    throw new Error(`Unknown gene panel configuration ${value}`);
  }
  const cfg = configurationMap[value];
  return { id: value, genes: cfg.genes, samples: cfg.samples };
}

export function findConfigurationIdByCounts(genes: number, samples: number): GenePanelConfigurationId | undefined {
  return genePanelConfigurationIds.find(id => {
    const cfg = configurationMap[id];
    return cfg.genes === genes && cfg.samples === samples;
  });
}

// Temporary gate for assays released in the workbook template before customer-wide launch.
export const softLaunchedGenePanelAssays = ['AY642', 'AY643', 'AY644'] as const;

const softLaunchedGenePanelAssaySet = new Set<string>(softLaunchedGenePanelAssays);

export function isSoftLaunchedGenePanelAssay(assay: string): boolean {
  return softLaunchedGenePanelAssaySet.has(assay);
}

export function getSoftLaunchedGenePanelAssays(assays: readonly string[]): string[] {
  return sortAssaysNumerically(assays.filter(isSoftLaunchedGenePanelAssay));
}

export function sortAssaysNumerically(assays: readonly string[]): string[] {
  const unique = Array.from(new Set(assays.map(a => a.trim()).filter(Boolean)));
  return unique.sort(compareAssayCodes);
}

function compareAssayCodes(a: string, b: string): number {
  const parsedA = parseAssayCode(a);
  const parsedB = parseAssayCode(b);
  if (parsedA.prefix !== parsedB.prefix) {
    return parsedA.prefix.localeCompare(parsedB.prefix);
  }
  if (parsedA.number !== parsedB.number) {
    return parsedA.number - parsedB.number;
  }
  return parsedA.suffix.localeCompare(parsedB.suffix);
}

function parseAssayCode(value: string) {
  const match = /^(\D*)(\d+)(.*)$/.exec(value);
  if (!match) {
    return { prefix: value, number: Number.MAX_SAFE_INTEGER, suffix: '' };
  }
  const [, prefix, digits, suffix] = match;
  return { prefix, number: parseInt(digits, 10), suffix };
}

export const defaultGenePanelKeys = ['ARG_2_1', 'ONE_HEALTH_96'] as const;
export type DefaultGenePanelKey = (typeof defaultGenePanelKeys)[number];

type DefaultPanelMembershipFlag = 'genePackage21' | 'genePackage3';

type DefaultGenePanelMeta = {
  key: DefaultGenePanelKey;
  name: string;
  configurationId: GenePanelConfigurationId;
  membershipFlag: DefaultPanelMembershipFlag;
  forceIncludeAssays: readonly string[];
  expectedAssayCount?: number;
};

const defaultGenePanelMetas: readonly DefaultGenePanelMeta[] = [
  {
    key: 'ARG_2_1',
    name: 'ARG 2.1',
    configurationId: '384:4',
    membershipFlag: 'genePackage21',
    forceIncludeAssays: ['AY1', 'AY315'], // AY315 stays selectable for legacy ARG 2.1 layouts even though it's deprecated
  },
  {
    key: 'ONE_HEALTH_96',
    name: '96 Gene Panel for One Health Framework',
    configurationId: '96:18',
    membershipFlag: 'genePackage3',
    forceIncludeAssays: ['AY1'],
  },
] as const;

export type DefaultGenePanelDefinition = {
  key: DefaultGenePanelKey;
  name: string;
  configurationId: GenePanelConfigurationId;
  assays: readonly string[];
};

export type DefaultGenePanelAssaySource = {
  assay: string;
  genePackage1?: boolean;
  genePackage2?: boolean;
  genePackage21?: boolean;
  genePackage3?: boolean;
  deprecated?: boolean;
};

export class DefaultGenePanelDefinitionError extends Error {
  constructor(
    public readonly meta: DefaultGenePanelMeta,
    public readonly expectedAssays: number,
    public readonly actualAssays: number,
  ) {
    super(`Default gene panel ${meta.name} expected ${expectedAssays} assays but got ${actualAssays}`);
    this.name = 'DefaultGenePanelDefinitionError';
  }
}

function shouldIncludeAssay(assay: DefaultGenePanelAssaySource, flag: DefaultPanelMembershipFlag): boolean {
  return Boolean((assay as Record<DefaultPanelMembershipFlag, unknown>)[flag]);
}

export function buildDefaultGenePanelDefinitions(
  assays: readonly DefaultGenePanelAssaySource[],
): DefaultGenePanelDefinition[] {
  const normalizedAssays = assays.map(row => ({ ...row, assay: row.assay.trim() })).filter(row => row.assay.length > 0);

  return defaultGenePanelMetas.map(meta => {
    const selection = new Set<string>();
    normalizedAssays.forEach(assay => {
      const forced = meta.forceIncludeAssays.includes(assay.assay);
      if (forced) {
        selection.add(assay.assay);
        return;
      }
      if (assay.deprecated) {
        return;
      }
      if (shouldIncludeAssay(assay, meta.membershipFlag)) {
        selection.add(assay.assay);
      }
    });
    meta.forceIncludeAssays.forEach(forceAssay => {
      if (forceAssay) {
        selection.add(forceAssay);
      }
    });
    const assaysForPanel = sortAssaysNumerically(Array.from(selection));
    const expectedGeneCount = meta.expectedAssayCount ?? getGenePanelConfiguration(meta.configurationId).genes;
    if (assaysForPanel.length !== expectedGeneCount) {
      throw new DefaultGenePanelDefinitionError(meta, expectedGeneCount, assaysForPanel.length);
    }
    return {
      key: meta.key,
      name: meta.name,
      configurationId: meta.configurationId,
      assays: assaysForPanel,
    } satisfies DefaultGenePanelDefinition;
  });
}

export function findDefaultGenePanelByAssays(
  definitions: readonly DefaultGenePanelDefinition[],
  assays: readonly string[],
): DefaultGenePanelDefinition | undefined {
  const normalized = sortAssaysNumerically(assays).join('|');
  for (const def of definitions) {
    if (sortAssaysNumerically(def.assays).join('|') === normalized) {
      return def;
    }
  }
  return undefined;
}

export const argSelectionSheetOrder = [
  'AY1',
  'AY600',
  'AY624',
  'AY27',
  'AY391',
  'AY392',
  'AY393',
  'AY2',
  'AY388',
  'AY3',
  'AY19',
  'AY394',
  'AY386',
  'AY18',
  'AY407',
  'AY8',
  'AY327',
  'AY333',
  'AY406',
  'AY4',
  'AY396',
  'AY6',
  'AY397',
  'AY398',
  'AY399',
  'AY400',
  'AY401',
  'AY402',
  'AY403',
  'AY385',
  'AY5',
  'AY404',
  'AY10',
  'AY16',
  'AY20',
  'AY410',
  'AY408',
  'AY409',
  'AY9',
  'AY329',
  'AY331',
  'AY11',
  'AY328',
  'AY411',
  'AY412',
  'AY15',
  'AY334',
  'AY413',
  'AY13',
  'AY414',
  'AY21',
  'AY415',
  'AY416',
  'AY330',
  'AY12',
  'AY418',
  'AY23',
  'AY17',
  'AY419',
  'AY420',
  'AY7',
  'AY332',
  'AY422',
  'AY421',
  'AY417',
  'AY423',
  'AY326',
  'AY389',
  'AY22',
  'AY24',
  'AY424',
  'AY14',
  'AY428',
  'AY390',
  'AY26',
  'AY335',
  'AY426',
  'AY427',
  'AY602',
  'AY603',
  'AY98',
  'AY100',
  'AY119',
  'AY144',
  'AY337',
  'AY338',
  'AY336',
  'AY442',
  'AY444',
  'AY340',
  'AY341',
  'AY454',
  'AY611',
  'AY445',
  'AY453',
  'AY143',
  'AY446',
  'AY109',
  'AY99',
  'AY339',
  'AY429',
  'AY116',
  'AY432',
  'AY121',
  'AY122',
  'AY123',
  'AY124',
  'AY134',
  'AY137',
  'AY149',
  'AY147',
  'AY117',
  'AY433',
  'AY118',
  'AY125',
  'AY605',
  'AY447',
  'AY448',
  'AY449',
  'AY130',
  'AY141',
  'AY150',
  'AY450',
  'AY440',
  'AY153',
  'AY146',
  'AY113',
  'AY451',
  'AY452',
  'AY101',
  'AY152',
  'AY102',
  'AY106',
  'AY120',
  'AY110',
  'AY612',
  'AY613',
  'AY601',
  'AY614',
  'AY435',
  'AY615',
  'AY616',
  'AY617',
  'AY145',
  'AY618',
  'AY108',
  'AY436',
  'AY103',
  'AY437',
  'AY140',
  'AY107',
  'AY604',
  'AY610',
  'AY126',
  'AY104',
  'AY135',
  'AY148',
  'AY438',
  'AY607',
  'AY431',
  'AY154',
  'AY608',
  'AY430',
  'AY439',
  'AY136',
  'AY127',
  'AY609',
  'AY105',
  'AY129',
  'AY128',
  'AY441',
  'AY115',
  'AY97',
  'AY114',
  'AY111',
  'AY112',
  'AY443',
  'AY384',
  'AY434',
  'AY133',
  'AY132',
  'AY139',
  'AY131',
  'AY138',
  'AY293',
  'AY289',
  'AY290',
  'AY296',
  'AY294',
  'AY500',
  'AY292',
  'AY207',
  'AY231',
  'AY343',
  'AY349',
  'AY351',
  'AY199',
  'AY200',
  'AY201',
  'AY355',
  'AY356',
  'AY359',
  'AY202',
  'AY483',
  'AY490',
  'AY203',
  'AY484',
  'AY491',
  'AY31',
  'AY234',
  'AY205',
  'AY35',
  'AY36',
  'AY41',
  'AY206',
  'AY492',
  'AY493',
  'AY219',
  'AY237',
  'AY208',
  'AY232',
  'AY357',
  'AY358',
  'AY360',
  'AY210',
  'AY350',
  'AY485',
  'AY209',
  'AY211',
  'AY239',
  'AY212',
  'AY345',
  'AY486',
  'AY214',
  'AY346',
  'AY213',
  'AY34',
  'AY33',
  'AY227',
  'AY215',
  'AY240',
  'AY216',
  'AY228',
  'AY229',
  'AY230',
  'AY223',
  'AY347',
  'AY348',
  'AY222',
  'AY224',
  'AY225',
  'AY482',
  'AY494',
  'AY495',
  'AY42',
  'AY238',
  'AY488',
  'AY233',
  'AY352',
  'AY217',
  'AY489',
  'AY354',
  'AY221',
  'AY497',
  'AY498',
  'AY499',
  'AY235',
  'AY353',
  'AY226',
  'AY501',
  'AY502',
  'AY521',
  'AY503',
  'AY504',
  'AY505',
  'AY317',
  'AY316',
  'AY318',
  'AY319',
  'AY320',
  'AY313',
  'AY314',
  'AY506',
  'AY310',
  'AY508',
  'AY509',
  'AY510',
  'AY511',
  'AY512',
  'AY507',
  'AY513',
  'AY514',
  'AY515',
  'AY516',
  'AY298',
  'AY517',
  'AY518',
  'AY311',
  'AY519',
  'AY520',
  'AY312',
  'AY309',
  'AY522',
  'AY307',
  'AY324',
  'AY321',
  'AY323',
  'AY322',
  'AY523',
  'AY315',
  'AY524',
  'AY299',
  'AY300',
  'AY301',
  'AY302',
  'AY303',
  'AY304',
  'AY305',
  'AY297',
  'AY526',
  'AY527',
  'AY306',
  'AY528',
  'AY47',
  'AY529',
  'AY48',
  'AY93',
  'AY94',
  'AY530',
  'AY55',
  'AY531',
  'AY45',
  'AY54',
  'AY543',
  'AY532',
  'AY86',
  'AY90',
  'AY56',
  'AY533',
  'AY547',
  'AY87',
  'AY545',
  'AY534',
  'AY43',
  'AY44',
  'AY544',
  'AY535',
  'AY46',
  'AY92',
  'AY548',
  'AY57',
  'AY74',
  'AY68',
  'AY546',
  'AY83',
  'AY53',
  'AY76',
  'AY75',
  'AY84',
  'AY536',
  'AY49',
  'AY50',
  'AY537',
  'AY89',
  'AY549',
  'AY550',
  'AY551',
  'AY538',
  'AY65',
  'AY59',
  'AY552',
  'AY539',
  'AY60',
  'AY62',
  'AY61',
  'AY85',
  'AY621',
  'AY66',
  'AY88',
  'AY58',
  'AY67',
  'AY553',
  'AY73',
  'AY554',
  'AY540',
  'AY69',
  'AY80',
  'AY70',
  'AY81',
  'AY51',
  'AY52',
  'AY77',
  'AY71',
  'AY79',
  'AY541',
  'AY542',
  'AY72',
  'AY78',
  'AY63',
  'AY64',
  'AY342',
  'AY82',
  'AY197',
  'AY198',
  'AY472',
  'AY190',
  'AY25',
  'AY344',
  'AY471',
  'AY470',
  'AY465',
  'AY287',
  'AY288',
  'AY247',
  'AY468',
  'AY187',
  'AY469',
  'AY185',
  'AY466',
  'AY193',
  'AY619',
  'AY467',
  'AY194',
  'AY195',
  'AY196',
  'AY191',
  'AY188',
  'AY620',
  'AY186',
  'AY189',
  'AY218',
  'AY220',
  'AY236',
  'AY487',
  'AY204',
  'AY184',
  'AY142',
  'AY473',
  'AY640',
  'AY629',
  'AY637',
  'AY475',
  'AY631',
  'AY638',
  'AY476',
  'AY639',
  'AY478',
  'AY632',
  'AY479',
  'AY628',
  'AY626',
  'AY625',
  'AY480',
  'AY627',
  'AY630',
  'AY555',
  'AY556',
  'AY38',
  'AY557',
  'AY559',
  'AY29',
  'AY30',
  'AY560',
  'AY558',
  'AY561',
  'AY562',
  'AY563',
  'AY37',
  'AY564',
  'AY566',
  'AY32',
  'AY565',
  'AY39',
  'AY40',
  'AY455',
  'AY456',
  'AY95',
  'AY96',
  'AY458',
  'AY457',
  'AY459',
  'AY460',
  'AY461',
  'AY463',
  'AY462',
  'AY246',
  'AY361',
  'AY362',
  'AY242',
  'AY245',
  'AY364',
  'AY243',
  'AY365',
  'AY244',
  'AY241',
  'AY474',
  'AY634',
  'AY477',
  'AY633',
  'AY641',
  'AY622',
  'AY635',
  'AY636',
  'AY248',
  'AY250',
  'AY280',
  'AY279',
  'AY249',
  'AY277',
  'AY278',
  'AY570',
  'AY272',
  'AY568',
  'AY576',
  'AY254',
  'AY255',
  'AY262',
  'AY256',
  'AY268',
  'AY282',
  'AY571',
  'AY252',
  'AY283',
  'AY366',
  'AY273',
  'AY572',
  'AY257',
  'AY261',
  'AY260',
  'AY573',
  'AY271',
  'AY258',
  'AY266',
  'AY367',
  'AY574',
  'AY265',
  'AY281',
  'AY368',
  'AY567',
  'AY253',
  'AY264',
  'AY259',
  'AY269',
  'AY276',
  'AY376',
  'AY270',
  'AY263',
  'AY267',
  'AY369',
  'AY575',
  'AY274',
  'AY275',
  'AY370',
  'AY371',
  'AY372',
  'AY577',
  'AY325',
  'AY373',
  'AY374',
  'AY375',
  'AY579',
  'AY284',
  'AY286',
  'AY578',
  'AY285',
  'AY583',
  'AY580',
  'AY581',
  'AY588',
  'AY582',
  'AY584',
  'AY585',
  'AY586',
  'AY589',
  'AY590',
  'AY591',
  'AY592',
  'AY593',
  'AY594',
  'AY595',
  'AY158',
  'AY383',
  'AY159',
  'AY378',
  'AY155',
  'AY156',
  'AY377',
  'AY171',
  'AY596',
  'AY172',
  'AY161',
  'AY597',
  'AY173',
  'AY163',
  'AY162',
  'AY164',
  'AY379',
  'AY174',
  'AY175',
  'AY176',
  'AY177',
  'AY165',
  'AY599',
  'AY178',
  'AY179',
  'AY380',
  'AY180',
  'AY157',
  'AY381',
  'AY160',
  'AY181',
  'AY167',
  'AY166',
  'AY598',
  'AY168',
  'AY170',
  'AY169',
  'AY182',
  'AY183',
  'AY382',
] as const;

const sheetOrderIndex = new Map<string, number>(argSelectionSheetOrder.map((assay, index) => [assay, index]));

export function sortAssaysBySheetOrder(assays: readonly string[]): string[] {
  const fallbackIndex = argSelectionSheetOrder.length;
  return [...assays].sort((a, b) => {
    const indexDiff = (sheetOrderIndex.get(a) ?? fallbackIndex) - (sheetOrderIndex.get(b) ?? fallbackIndex);
    if (indexDiff !== 0) {
      return indexDiff;
    }
    return compareAssayCodes(a, b);
  });
}

export function normalizeAssaysForPersistence(assays: readonly string[]): string[] {
  return sortAssaysNumerically(assays);
}
